import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import {
  EMPTY,
  Observable,
  Subscription,
  Subject,
  concatMap,
  debounceTime,
  from,
} from 'rxjs';
import { Paginator } from '../../models/paginator';

export type RetrieveFn<TDoc, TFilters> = (
  page: number,
  limit: number,
  offset: number,
  sort: string | undefined,
  filters: TFilters,
) => Promise<Paginator<TDoc>>;

export interface PaginatedListManagerOptions<TDoc, TFilters> {
  destroyRef: DestroyRef;
  initialFilters: TFilters;
  retrieveFn: RetrieveFn<TDoc, TFilters>;
  initialSort?: string;
  onError?: (err: unknown) => void;
  onLoadingStart?: () => void;
  onLoadingEnd?: () => void;
  filterDebounceMs?: number;
}

const DEFAULT_SORT = 'createdAt _id' as const;

/**
 * Encapsulates the pagination/filtering/sorting shared by the crawl and
 * discovery step tables
 */
export class PaginatedListManager<TDoc, TFilters extends Record<string, unknown>> {
  loading = false;
  done = false;

  paginator: Paginator<TDoc> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0,
    sort: DEFAULT_SORT,
  };

  filters: TFilters;

  private readonly filtersSubject = new Subject<TFilters>();
  private readonly destroyRef: DestroyRef;
  private autoRefreshSubscription?: Subscription;
  // Counts retrieves still awaiting the server so the auto refresh poll never
  // stacks on top of an in-flight fetch (user paging, filter changes, another
  // tick)
  private inFlight = 0;

  private readonly retrieveFn: RetrieveFn<TDoc, TFilters>;
  private readonly onError: (err: unknown) => void;
  private readonly onLoadingStart: () => void;
  private readonly onLoadingEnd: () => void;

  constructor(options: PaginatedListManagerOptions<TDoc, TFilters>) {
    this.filters = options.initialFilters;
    this.destroyRef = options.destroyRef;
    if (options.initialSort) {
      this.paginator.sort = options.initialSort;
    }
    this.retrieveFn = options.retrieveFn;
    this.onError = options.onError ?? (() => {});
    this.onLoadingStart = options.onLoadingStart ?? (() => {});
    this.onLoadingEnd = options.onLoadingEnd ?? (() => {});

    this.filtersSubject
      .pipe(
        debounceTime(options.filterDebounceMs ?? 300),
        takeUntilDestroyed(options.destroyRef),
      )
      .subscribe(async () => {
        this.resetPage();
        await this.retrieve();
      });
  }

  async init(): Promise<void> {
    this.loading = true;
    this.done = false;
    await this.retrieve();
    this.loading = false;
    this.done = true;
  }

  async retrieve(): Promise<void> {
    this.inFlight++;
    try {
      this.onLoadingStart();
      const results = await this.retrieveFn(
        this.paginator.page,
        this.paginator.limit,
        this.paginator.offset,
        this.paginator.sort,
        this.filters,
      );
      if (this.paginator.page === results.page) {
        this.paginator = {
          ...this.paginator,
          ...results,
        };
      }
    } catch (err) {
      this.onError(err);
    } finally {
      this.inFlight--;
      this.onLoadingEnd();
    }
  }

  get isRetrieving(): boolean {
    return this.inFlight > 0;
  }

  resetPage(): void {
    this.paginator.limit = this.paginator.limit || 10;
    this.paginator.page = 1;
    this.paginator.offset = 0;
  }

  async handlePage(event: PageEvent): Promise<void> {
    this.paginator.limit = event.pageSize;
    this.paginator.page = event.pageIndex + 1;
    this.paginator.offset = event.pageIndex * this.paginator.limit;
    await this.retrieve();
  }

  async onSortChange(event: Sort): Promise<void> {
    if (['asc', 'desc'].includes(event.direction)) {
      this.paginator.sort = `${event.direction === 'desc'
        ? '-'
        : ''}${event.active}`;
    } else if (event.direction === '') {
      this.paginator.sort = DEFAULT_SORT;
    }
    await this.retrieve();
  }

  // debounced update (search)
  updateFilters(patch: Partial<TFilters>): void {
    this.filters = { ...this.filters, ...patch };
    this.filtersSubject.next(this.filters);
  }

  // immediate update + refetch (dropdown)
  async updateFiltersNow(patch: Partial<TFilters>): Promise<void> {
    this.filters = { ...this.filters, ...patch };
    this.resetPage();
    await this.retrieve();
  }

  startAutoRefresh(active$: Observable<boolean>): void {
    this.stopAutoRefresh();
    // The source (e.g. the eligibility/batch poll) already emits on its own
    // cadence, so each emission is the refresh trigger - no internal timer.
    // The in-flight guard drops a tick while a fetch is still running, except
    // the final settled state (active = false): its retrieve always runs, after
    // the running one, so the list ends on the finished crawl/discovery/analysis
    // state instead of the last intermediate one.
    this.autoRefreshSubscription = active$.pipe(
      concatMap((active) => active && this.isRetrieving ? EMPTY : from(this.retrieve())),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  stopAutoRefresh(): void {
    this.autoRefreshSubscription?.unsubscribe();
    this.autoRefreshSubscription = undefined;
  }
}
